import { lowerCase } from 'lodash-es';
import titlecase from 'titlecase';

const DEFAULT_TOLERANCE = 0.6;

export function isCloseMatch(
  queryString: string,
  comparisonString: string,
  tolerance = DEFAULT_TOLERANCE
) {
  const lowerCaseQueryStr = lowerCase(queryString);
  const lowerCaseCompareStr = lowerCase(comparisonString);

  const queryStringIsSubsetOfComparisonString =
    lowerCaseQueryStr.search(lowerCaseCompareStr) !== -1;
  if (queryStringIsSubsetOfComparisonString) return true;

  if (tolerance >= 0 && tolerance <= 1) {
    const queryStringIsSimilarToComparisonString =
      compareTwoStrings(lowerCaseQueryStr, lowerCaseCompareStr) > tolerance;
    if (queryStringIsSimilarToComparisonString) return true;
  } else {
    throw new Error(
      'isCloseMatch error! Tolerance value must be a decimal between 0 and 1 inclusive.'
    );
  }

  return false;
}

/**
 * Sørensen–Dice coefficient of the two strings' bigrams, ignoring whitespace:
 * 1 for identical strings, 0 for strings with no bigram in common. Ported
 * from `string-similarity`'s `compareTwoStrings`.
 */
function compareTwoStrings(first: string, second: string): number {
  const a = first.replace(/\s+/g, '');
  const b = second.replace(/\s+/g, '');

  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;

  const bigramCounts = new Map<string, number>();
  for (let i = 0; i < a.length - 1; i++) {
    const bigram = a.substring(i, i + 2);
    bigramCounts.set(bigram, (bigramCounts.get(bigram) ?? 0) + 1);
  }

  let intersectionSize = 0;
  for (let i = 0; i < b.length - 1; i++) {
    const bigram = b.substring(i, i + 2);
    const count = bigramCounts.get(bigram) ?? 0;
    if (count > 0) {
      bigramCounts.set(bigram, count - 1);
      intersectionSize++;
    }
  }

  return (2 * intersectionSize) / (a.length + b.length - 2);
}

/**
 * Capitalizes the first letter of each word with a few important exceptions:
 * https://johnresig.com/blog/title-capitalization-in-javascript/
 */
export function smartTitlecase(str: string): string {
  return titlecase(str);
}
